import { InstantInterval, PlainDateInterval, PlainTimeInterval } from '@airreading/common';
import { DayOfWeek, DayOfWeekLabelMap, MeetingBlockLike, ScheduleBreakLike } from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

export const formatSchedule = () => {};

export const dayLabelToDaysEnum: Record<string, DayOfWeek> = {
  monday: DayOfWeek.monday,
  tuesday: DayOfWeek.tuesday,
  wednesday: DayOfWeek.wednesday,
  thursday: DayOfWeek.thursday,
  friday: DayOfWeek.friday,
  saturday: DayOfWeek.saturday,
  sunday: DayOfWeek.sunday,

  mon: DayOfWeek.monday,
  tue: DayOfWeek.tuesday,
  wed: DayOfWeek.wednesday,
  thu: DayOfWeek.thursday,
  fri: DayOfWeek.friday,
  sat: DayOfWeek.saturday,
  sun: DayOfWeek.sunday,
};

export const to12Hour = (hour: number): { hourIn12: number; ampm: 'AM' | 'PM' } => {
  const hourIn12 = hour % 12 || 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return { hourIn12, ampm };
};

/**
 *
 * @param input input format: 'Day of week, HH:MM - HH:MM', e.g. 'Mon, 10:00 - 12:00'
 * @returns parsed meeting block and return parsed result, or return error msg
 */
export function parseIntoMeetingBlock(input: string): MeetingBlockLike | string {
  const [dayOfWeek, timeRange] = input.split(',').map((val) => val.trim());

  if (!dayLabelToDaysEnum[dayOfWeek.toLowerCase()]) {
    return `${input} is invalid. ${dayOfWeek} is not a valid day of week`;
  }

  if (!timeRange) {
    return `${input} is invalid. Empty time range`;
  }

  const [startTime, endTime] = timeRange.split('-').map((val) => val.trim());

  try {
    const interval = PlainTimeInterval.from(startTime, endTime);
    const { start, end } = interval;

    return {
      day: dayLabelToDaysEnum[dayOfWeek.toLowerCase()],
      startPlainTime: start,
      endPlainTime: end,
    };
  } catch (e: unknown) {
    if (e instanceof RangeError) {
      return e.message;
    }
    return 'Unknown error';
  }
}

export function parseIntoPlainTimeInterval(input: string): PlainTimeInterval | string {
  const [startTime, endTime] = input.split('-').map((val) => val.trim());

  if (!startTime || !endTime) {
    return `${input} is invalid. Empty start/end time`;
  }

  try {
    const interval = PlainTimeInterval.from(startTime, endTime);
    return interval;
  } catch (e: unknown) {
    if (e instanceof RangeError) {
      return e.message;
    }
    return 'Unknown error';
  }
}

const scheduleBreakDatePattern = /(?<month>\d{2})\/(?<day>\d{2})\/(?<year>\d{4})/;

/**
 *
 * @param input format: 'MM/DD/YYYY - MM/DD/YYYY', e.g. '01/01/2022 - 01/01/2023'
 */
export function parseIntoScheduleBreak(input: string): ScheduleBreakLike | string {
  const dateRange = input.trim();

  const [from, to] = dateRange.split('-').map((val) => val.trim());
  if (!scheduleBreakDatePattern.test(from) || !scheduleBreakDatePattern.test(to)) {
    return `${input} is invalid. Empty or invalid start/end date format. Need MM/DD/YYYY`;
  }

  function extract(s: string): { month: number; day: number; year: number } {
    const result = s.match(scheduleBreakDatePattern);
    if (!result || !result.groups) {
      throw new RangeError(`Invalid date format ${s}. Need MM/DD/YYYY`);
    }

    const month = Number(result.groups['month']);
    const day = Number(result.groups['day']);
    const year = Number(result.groups['year']);
    return { month, day, year };
  }

  try {
    const startDate = Temporal.PlainDate.from(extract(from));
    const endDate = Temporal.PlainDate.from(extract(to));
    const { start, end } = PlainDateInterval.from(startDate, endDate);

    return {
      name: null,
      startPlainDate: start,
      endPlainDate: end,
    };
  } catch (e: unknown) {
    if (e instanceof RangeError) {
      return e.message;
    }
    return 'Unknown error';
  }
}

/**
 * Get items within the last N days.
 */
export function filterByStartInInterval<Item extends { startInstant: Temporal.Instant }>(
  items: Item[],
  interval: InstantInterval,
): Item[] {
  return items.filter((item) => interval.contains(item.startInstant));
}

export function getDayOfWeekOptions(useFullName: boolean = false) {
  return Object.values(DayOfWeek).map((value) => ({
    value,
    label: DayOfWeekLabelMap[value][useFullName ? 'full' : 'abbr'],
  }));
}
